import { FC, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useMutation, useLazyQuery } from '@apollo/client'
import { Tab } from 'react-bootstrap'
import Button from 'react-bootstrap/Button'
import { useAppDispatch, useAppSelector } from 'hooks/store'
import {
  getEmptyDocumentProcessing,
  setDocumentProcessing,
  setSelectedDocumentId,
  setSelectedDocument,
  setTabReview,
  setTabSubmit,
  setSelectedDocumentChecklistItemId,
  setSelectedDocumentChecklistItem
} from 'reducers/document/documentProcessingSlice'
import { setIsCompleteAllChecklistUploadedDocuments } from 'reducers/activecase/activecaseSlice'
import GoBackHeader from 'components/organisms/GoBackHeader'
import CaseLevelHeader from 'components/features/dashboard/CaseLevelHeader'
import {
  GET_AIDOCUMENT_DASHBOARD,
  GetAiDocumentDashboardQueryResponse
} from 'share/queries/documents'
import {
  IdpProcessDocumentsResponse,
  IdpProcessDocumentsVariables,
  IDP_PROCESS_DOCUMENTS
} from 'share/mutations/documents'

import {
  UpdateCompleteAllChecklistDocsResponse,
  UpdateCompleteAllChecklistDocsVariables,
  UPDATE_IS_COMPLETE_ALL_CHECKLIST_UPLOADED_DOCUMENTS_FLAG
} from 'share/mutations/cases'

import {
  DocumentReview,
  DocumentRequestChecklistItem
} from 'share/domain/document'
import { formatDate } from 'share/utils/utils'
import {
  SetOpenedDocResponse,
  SetOpenedDocVariables,
  SET_OPENED_DOC
} from 'share/mutations/documents'
import StyledTabs from 'components/molecules/StyledTabs'
import TemplateView from 'components/organisms/TemplateView'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Tooltip } from 'primereact/tooltip'

import { DataTableSelectionSingleChangeEvent } from 'primereact/datatable'
import DocumentsAddReplaceModal from '../DocumentsAddReplaceModal'
import SpinnerEllipsis from 'components/atoms/SpinnerEllipsis'

import './styles.scss'
import 'primeicons/primeicons.css'

interface IHistory {
  caseId?: number
}

const truncateFileName = (text: string) => {
  if (text.length > 20) {
    const start = text.substring(0, 14)
    const end = text.substring(text.length - 5)
    const newDocText = `${start}...${end}`
    return newDocText
  }

  return text
}

const AiDocuments: FC = () => {
  const history = useHistory<IHistory>()
  const dispatch = useAppDispatch()
  const activeCase = useAppSelector((state) => state.activeCase)
  const [requeryAiDashboard, setRequeryAiDashboard] = useState(1)
  const [startIdpDocProcessing, setStartIdpDocProcessing] = useState(false)
  const [disableUploadDocButton, setDisableUploadDocButton] = useState(false)
  const [showDocumentsAddReplaceModal, setShowDocumentsAddReplaceModal] =
    useState(false)
  const [isHomePage, setIsHomePage] = useState(false)

  const [reviewDocuments, setReviewDocuments] = useState<DocumentReview[]>([])
  const documentProcessing = useAppSelector((state) => state.documentProcessing)
  const [key, setKey] = useState(() =>
    documentProcessing.activeTab === 'submit' ? 'submit' : 'review'
  )

  const [isLoading, setIsLoading] = useState(false)
  const [selectedChecklistItem, setSelectedChecklistItem] =
    useState<DocumentRequestChecklistItem | null>(null)

  const [getAiDocumentDashboard, { loading, data, error }] =
    useLazyQuery<GetAiDocumentDashboardQueryResponse>(
      GET_AIDOCUMENT_DASHBOARD,
      {
        fetchPolicy: 'network-only'
      }
    )

  const [setOpenedDoc] =
    useMutation<SetOpenedDocResponse, SetOpenedDocVariables>(SET_OPENED_DOC)

  const [idpProcessDocumentsMutation, { loading: idpProcessDocumentsLoading }] =
    useMutation<IdpProcessDocumentsResponse, IdpProcessDocumentsVariables>(
      IDP_PROCESS_DOCUMENTS
    )

  const [
    updateCompleteAllChecklistDocsMutation,
    { loading: completeAllChecklistDocsLoading }
  ] = useMutation<
    UpdateCompleteAllChecklistDocsResponse,
    UpdateCompleteAllChecklistDocsVariables
  >(UPDATE_IS_COMPLETE_ALL_CHECKLIST_UPLOADED_DOCUMENTS_FLAG)

  const [documentChecklistItems, setDocumentChecklistItems] = useState<
    DocumentRequestChecklistItem[]
  >([])

  useEffect(() => {
    if (activeCase && requeryAiDashboard) {
      if (activeCase.matter) {
        if (activeCase.matter.id) {
          getAiDocumentDashboard({
            variables: { caseId: activeCase.matter.id }
          })
        }
      }
    }
  }, [activeCase, requeryAiDashboard])

  useEffect(() => {
    if (activeCase?.matter) {
      if (activeCase.matter.isAiEnabled && activeCase.matter.isChatBotEnabled) {
        setIsHomePage(false)
      } else if (activeCase.matter.isAiEnabled) {
        setIsHomePage(true)
      } else {
        setIsHomePage(false)
      }
    }
  }, [activeCase])

  useEffect(() => {
    if (startIdpDocProcessing) {
      processUploadDocsWithIdp()
    }
  }, [startIdpDocProcessing])

  useEffect(() => {
    if (
      loading ||
      idpProcessDocumentsLoading ||
      completeAllChecklistDocsLoading
    ) {
      setIsLoading(true)
    } else {
      setIsLoading(false)
    }
  }, [loading, idpProcessDocumentsLoading, completeAllChecklistDocsLoading])

  useEffect(() => {
    if (data && data?.documents.aiDocumentDashboard?.review.length > 0) {
      setReviewDocuments(data?.documents.aiDocumentDashboard.review)
      // intialize the redux store to empty everytime we come to this page
      dispatch(setDocumentProcessing(getEmptyDocumentProcessing()))
      if (key === 'review') {
        dispatch(setTabReview())
      }
    }
    if (data && data?.documents.aiDocumentDashboard?.checkList.length > 0) {
      setDocumentChecklistItems(data.documents.aiDocumentDashboard.checkList)
    }
  }, [data])

  if (error) {
    return <div>{error?.message}</div>
  }

  async function processUploadDocsWithIdp() {
    const uploadedDocumentIdList: number[] = []
    documentProcessing.documentUploadList.map((d) => {
      uploadedDocumentIdList.push(d.id)
    })

    const input = {
      caseId:
        activeCase.matter && activeCase.matter.id ? activeCase?.matter.id : 0,
      uploadedDocumentIdList: uploadedDocumentIdList,
      replaceMatterClientPortalDocumentId: null,
      replaceMatterDocumentCheckListItemId: null
    }

    const idpProcessResult = await idpProcessDocumentsMutation({
      variables: { request: input }
    })

    console.log(idpProcessResult.data?.documents.idpProcessDocuments.success)

    setDisableUploadDocButton(false)
    setStartIdpDocProcessing(false)
    setRequeryAiDashboard(requeryAiDashboard + 1)
  }

  function handleSelect(tabKey: string | null) {
    if (tabKey === key) return

    setKey(tabKey ? tabKey : 'submit')

    if (key === 'submit') {
      dispatch(setTabReview())
    } else {
      dispatch(setTabSubmit())
    }
  }

  function handleSelectChecklistItem(item: DocumentRequestChecklistItem) {
    if (item.documentCount > 0) {
      dispatch(setSelectedDocumentChecklistItemId(item.id))
      dispatch(setSelectedDocumentChecklistItem(item))
      history.push('/dashboard/documents/checklistitem')
    }
  }

  const onRowSelect = (
    event: DataTableSelectionSingleChangeEvent<DocumentRequestChecklistItem[]>
  ) => {
    setSelectedChecklistItem(event.value) // event.value contains the selected row data
    // Trigger your action here with event.value
    handleSelectChecklistItem(event.value)
    // Example: call an API, open a dialog, etc.
  }

  const handleIconClick = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    rowData: DocumentRequestChecklistItem
  ) => {
    // Stop the event from propagating up to the onRowSelect handler
    event.stopPropagation()

    // Navigate to the details page for the specific row item
    if (rowData.documentCount > 0) {
      dispatch(setSelectedDocumentChecklistItemId(rowData.id))
      dispatch(setSelectedDocumentChecklistItem(rowData))
      history.push('/dashboard/documents/submission-summary')
    }
  }

  const commentBodyTemplate = (rowData: DocumentRequestChecklistItem) => {
    if (
      rowData.comment &&
      rowData.hasWarnings &&
      rowData.status !== 'In Progress'
    ) {
      return (
        <>
          <div>
            {rowData.name}{' '}
            <i
              className='pi pi-exclamation-circle my-icon2'
              data-pr-tooltip='Issues'
              data-pr-position='right'
              onClick={(e) => handleIconClick(e, rowData)}
              style={{ color: 'red', border: 'none' }}
            ></i>
            <Tooltip target='.my-icon2' className='white-tooltip' />{' '}
            <i
              className='pi pi-comments p-mr-2 my-icon'
              data-pr-tooltip={rowData.comment}
              data-pr-position='right'
            ></i>
            <Tooltip target='.my-icon' className='white-tooltip' />{' '}
          </div>
          <div>{rowData.durationDescription}</div>
        </>
      )
    } else if (rowData.comment) {
      return (
        <>
          <div>
            {rowData.name}{' '}
            <i
              className='pi pi-comments p-mr-2 my-icon'
              data-pr-tooltip={rowData.comment}
              data-pr-position='right'
            ></i>
            <Tooltip target='.my-icon' className='white-tooltip' />{' '}
          </div>
          <div>{rowData.durationDescription}</div>
        </>
      )
    } else if (rowData.hasWarnings && rowData.status !== 'In Progress') {
      return (
        <>
          <div>
            {rowData.name}{' '}
            <i
              className='pi pi-exclamation-circle my-icon2'
              data-pr-tooltip='Issues'
              data-pr-position='right'
              onClick={(e) => handleIconClick(e, rowData)}
              style={{ color: 'red', border: 'none' }}
            ></i>
            <Tooltip target='.my-icon2' className='white-tooltip' />
          </div>
          <div>{rowData.durationDescription}</div>
        </>
      )
    } else {
      return (
        <>
          <div>{rowData.name}</div>
          <div>{rowData.durationDescription}</div>
        </>
      )
    }
  }

  const handleShowDocumentsAddReplaceModal = () => {
    setDisableUploadDocButton(true)
    setShowDocumentsAddReplaceModal(true)
  }

  const handleCloseDocumentsAddReplaceModal = () => {
    setStartIdpDocProcessing(true)
    setShowDocumentsAddReplaceModal(false)
  }

  const renderClassifyMessage = (): JSX.Element => {
    return (
      <SpinnerEllipsis
        showText={true}
        text='Your documents are being classified'
      />
    )
  }

  function getIsCompleteAllChecklistUploadedDocuments() {
    if (activeCase) {
      if (activeCase.matter) {
        return activeCase.matter.isCompleteAllChecklistUploadedDocuments
      }
    }
    return false
  }

  async function handleAllChecklistCompleteChange(
    event: React.ChangeEvent<HTMLInputElement>
  ) {
    const input = {
      caseId:
        activeCase.matter && activeCase.matter.id ? activeCase?.matter.id : 0,
      isCompleteAllChecklistUploadedDocuments: event.target.checked
    }

    await updateCompleteAllChecklistDocsMutation({
      variables: { request: input }
    })

    dispatch(
      setIsCompleteAllChecklistUploadedDocuments(
        input.isCompleteAllChecklistUploadedDocuments
      )
    )
  }

  if (startIdpDocProcessing) {
    return renderClassifyMessage()
  }

  return (
    <>
      {isLoading && loadingSpinnerPortal()}
      {!isHomePage && <GoBackHeader text='Documents' />}
      {isHomePage && (
        <CaseLevelHeader
          text={activeCase.matter?.firm.name}
          status={
            activeCase.matter?.dashBoardHeaderDescription
              ? activeCase.matter?.dashBoardHeaderDescription
              : ''
          }
          iconUrl={activeCase.matter?.firm.iconUrl}
        />
      )}
      <TemplateView title=''>
        <StyledTabs
          defaultActiveKey='submit'
          id='ui-docs-tabs'
          onSelect={handleSelect}
          activeKey={key}
        >
          <Tab eventKey='submit' title='Document Flow Checklist'>
            <Button
              data-testid='documents-upload-documents-button'
              disabled={disableUploadDocButton}
              type='button'
              onClick={handleShowDocumentsAddReplaceModal}
            >
              Upload Documents
            </Button>

            <div className='card'>
              <DataTable
                value={documentChecklistItems}
                selectionMode='single'
                selection={selectedChecklistItem}
                onSelectionChange={onRowSelect}
                dataKey='id'
                tableStyle={{ minWidth: '5rem' }}
                className='black-header-flow'
              >
                <Column
                  field='name'
                  header='Name'
                  body={commentBodyTemplate}
                  sortable
                  pt={{
                    sortIcon: {
                      className: `${'flow-sort-color'}`
                    }
                  }}
                ></Column>
                <Column
                  field='status'
                  header='Status'
                  sortable
                  pt={{
                    sortIcon: {
                      className: `${'flow-sort-color'}`
                    }
                  }}
                ></Column>
                <Column field='documentCount' header=' '></Column>
              </DataTable>
            </div>
            <div className='mt-3'>
              <label>
                <input
                  type='checkbox'
                  checked={getIsCompleteAllChecklistUploadedDocuments()}
                  onChange={handleAllChecklistCompleteChange}
                  className='mr-2'
                />
                <span>
                  Check this box after uploading all requested documents
                </span>
              </label>
            </div>
          </Tab>
          <Tab eventKey='review' title='Review Documents From Your Attorney'>
            <ul className='ui-review-docs mb-0'>
              {reviewDocuments.length === 0 && (
                <div>No Documents to Review from your attorney</div>
              )}
              {reviewDocuments.map((doc) => {
                return (
                  <li
                    key={doc.id}
                    onClick={() => {
                      dispatch(setSelectedDocumentId(doc.id))
                      dispatch(setSelectedDocument(doc))
                      setOpenedDoc({
                        variables: { documentId: doc.id }
                      })
                        .then((res) => console.log(res))
                        .catch((err) => console.log(err))
                      history.push('/dashboard/documents/review-submit-detail')
                    }}
                  >
                    <div className={`${!doc.opened ? 'unread' : 'read'}`}>
                      <p>{truncateFileName(doc.name)}</p>
                      {doc.comments.length > 0 && (
                        <span>
                          {doc.comments.length + ' '}
                          Comment{doc.comments.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    <span>{formatDate(doc.uploaded)}</span>
                  </li>
                )
              })}
            </ul>
          </Tab>
        </StyledTabs>
      </TemplateView>
      <DocumentsAddReplaceModal
        businessObjectId={null}
        businessObjectType={'ignorechangetracking'}
        replacedDocumentId={null}
        show={showDocumentsAddReplaceModal}
        title='Upload Documents'
        onHide={handleCloseDocumentsAddReplaceModal}
        folderName='Important: Upload all images for a document at the same time to ensure proper processing.'
        folderDescription=''
        isAiCase={true}
      />
    </>
  )
}

export default AiDocuments
